import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getCsrfToken } from '../utils/auth';
import { ProductIcon } from '../components/BrandIcons';
import '../styles/ProductKnowledge.css';

const jsonHeaders = () => ({ 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() });

// One product (design §3.5): its component projects, its sources, and its knowledge
// document with a rendered View / raw-Markdown Edit toggle. Regeneration proposes a new
// document when the current one was hand-edited, never overwriting it silently.
export default function ProductKnowledge({ isAdmin = false }) {
  const navigate = useNavigate();
  const { productId } = useParams();

  const [product, setProduct] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState('');           // '', 'generating', 'saving'

  const [docMode, setDocMode] = useState('view');  // 'view' | 'edit'
  const [draftDoc, setDraftDoc] = useState('');
  const [proposal, setProposal] = useState(null);  // regenerated doc awaiting accept/discard

  // Add-source form
  const [srcKind, setSrcKind] = useState('text');
  const [srcTitle, setSrcTitle] = useState('');
  const [srcText, setSrcText] = useState('');
  const [srcUrl, setSrcUrl] = useState('');
  const [srcFile, setSrcFile] = useState(null);
  const [orcProject, setOrcProject] = useState('');
  const [orcType, setOrcType] = useState('');
  const [orcFilter, setOrcFilter] = useState('');
  const [addingSource, setAddingSource] = useState(false);

  // Edit an existing text source (inline) + view any source's stored text
  const [editSrcId, setEditSrcId] = useState(null);
  const [editSrcTitle, setEditSrcTitle] = useState('');
  const [editSrcText, setEditSrcText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [viewSource, setViewSource] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [prod, projs] = await Promise.all([
        fetch(`/api/knowledge/products/${productId}`, { credentials: 'include' })
          .then(r => { if (!r.ok) throw new Error('Product not found'); return r.json(); }),
        fetch('/api/config/projects', { credentials: 'include' }).then(r => r.json()),
      ]);
      setProduct(prod);
      setDraftDoc(prod.document_md || '');
      setProjects(projs.projects || []);
      setError(null);
    } catch (e) {
      setError(e.message || 'Could not load product.');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  const distinctProjects = useMemo(() => {
    const seen = new Map();
    for (const p of projects) {
      const id = String(p.project_id);
      if (!seen.has(id)) seen.set(id, { id, name: p.project_name || `Project ${id}` });
    }
    return seen;
  }, [projects]);

  const nameOf = useCallback(id => distinctProjects.get(String(id))?.name || `Project ${id}`, [distinctProjects]);

  // For the Orcanos source picker: the chosen project's item types + a version to query.
  const orcProjectRow = useMemo(
    () => projects.find(p => String(p.project_id) === String(orcProject)),
    [projects, orcProject],
  );
  const orcItemTypes = useMemo(() => orcProjectRow?.item_types || [], [orcProjectRow]);

  // ------------------------------------------------------------ sources

  const addSource = async () => {
    setAddingSource(true);
    setError(null);
    try {
      let res;
      if (srcKind === 'file') {
        if (!srcFile) throw new Error('Choose a file to upload.');
        const form = new FormData();
        form.append('file', srcFile);
        form.append('title', srcTitle || '');
        res = await fetch(`/api/knowledge/products/${productId}/sources/upload`, {
          method: 'POST', credentials: 'include',
          headers: { 'X-CSRF-Token': getCsrfToken() },   // no Content-Type — the browser sets the multipart boundary
          body: form,
        });
      } else {
        let body;
        if (srcKind === 'text') {
          if (!srcText.trim()) throw new Error('Paste some text for this source.');
          body = { kind: 'text', title: srcTitle || 'Pasted text', content: srcText };
        } else if (srcKind === 'url') {
          if (!srcUrl.trim()) throw new Error('Enter a URL to fetch.');
          body = { kind: 'url', title: srcTitle || undefined, url: srcUrl.trim() };
        } else {
          if (!orcProject || !orcType || !orcFilter) throw new Error('Pick a project, item type, and filter.');
          body = {
            kind: 'orcanos', title: srcTitle || undefined,
            project_id: orcProject, version_id: orcProjectRow?.version_id,
            item_type: orcType, filter_id: parseInt(orcFilter, 10),
          };
        }
        res = await fetch(`/api/knowledge/products/${productId}/sources`, {
          method: 'POST', credentials: 'include', headers: jsonHeaders(), body: JSON.stringify(body),
        });
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not add source');
      setSrcText(''); setSrcTitle(''); setOrcFilter(''); setSrcUrl(''); setSrcFile(null);
      await load();
      if (data.truncated) {
        setError(`Added, but only the first ${data.char_count.toLocaleString()} characters of text were kept. `
          + 'Split the file to include the rest.');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setAddingSource(false);
    }
  };

  const toggleInclude = async (source) => {
    await fetch(`/api/knowledge/products/${productId}/sources/${source.id}`, {
      method: 'PATCH', credentials: 'include', headers: jsonHeaders(),
      body: JSON.stringify({ include: !source.include }),
    });
    load();
  };

  const deleteSource = async (source) => {
    if (!window.confirm(`Remove source "${source.title}"?`)) return;
    await fetch(`/api/knowledge/products/${productId}/sources/${source.id}`, {
      method: 'DELETE', credentials: 'include', headers: { 'X-CSRF-Token': getCsrfToken() },
    });
    load();
  };

  const startEditSource = (source) => {
    setEditSrcId(source.id);
    setEditSrcTitle(source.title || '');
    setEditSrcText(source.content || '');
  };

  const cancelEditSource = () => { setEditSrcId(null); setEditSrcTitle(''); setEditSrcText(''); };

  const saveSourceEdit = async () => {
    if (!editSrcText.trim()) { setError('Source text cannot be empty.'); return; }
    setSavingEdit(true);
    setError(null);
    try {
      const res = await fetch(`/api/knowledge/products/${productId}/sources/${editSrcId}`, {
        method: 'PATCH', credentials: 'include', headers: jsonHeaders(),
        body: JSON.stringify({ title: editSrcTitle || 'Pasted text', content: editSrcText }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not save source');
      cancelEditSource();
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingEdit(false);
    }
  };

  // Add the generated knowledge document back as a (text) source. Defaults to
  // NOT included, so it can't circularly feed its own next regeneration.
  const addDocAsSource = async () => {
    const doc = product.document_md || '';
    if (!doc.trim()) return;
    const stamp = new Date().toISOString().slice(0, 10);
    setError(null);
    try {
      const res = await fetch(`/api/knowledge/products/${productId}/sources`, {
        method: 'POST', credentials: 'include', headers: jsonHeaders(),
        body: JSON.stringify({
          kind: 'text', title: `Knowledge document (snapshot ${stamp})`,
          content: doc, include: false,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not add source');
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const downloadSourceText = (source) => {
    const blob = new Blob([source.content || ''], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safe = (source.title || 'source').replace(/[^\w.\- ]+/g, '_').slice(0, 80);
    a.href = url; a.download = `${safe}.txt`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  // ------------------------------------------------------------ document

  const generate = async () => {
    setBusy('generating');
    setError(null);
    setProposal(null);
    try {
      const res = await fetch(`/api/knowledge/products/${productId}/generate`, {
        method: 'POST', credentials: 'include', headers: jsonHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Generation failed');
      if (data.proposal) {
        setProposal(data.document_md);       // don't overwrite the hand-edited doc; let the user choose
      } else {
        await load();
        setDocMode('view');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const acceptProposal = async () => {
    setBusy('saving');
    try {
      await fetch(`/api/knowledge/products/${productId}`, {
        method: 'PUT', credentials: 'include', headers: jsonHeaders(),
        body: JSON.stringify({ document_md: proposal }),
      });
      setProposal(null);
      await load();
      setDocMode('view');
    } finally {
      setBusy('');
    }
  };

  const saveDoc = async () => {
    setBusy('saving');
    setError(null);
    try {
      const res = await fetch(`/api/knowledge/products/${productId}`, {
        method: 'PUT', credentials: 'include', headers: jsonHeaders(),
        body: JSON.stringify({ document_md: draftDoc }),
      });
      if (!res.ok) throw new Error('Save failed');
      await load();
      setDocMode('view');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const deleteProduct = async () => {
    if (!window.confirm(`Delete product "${product.name}"? Its sources and document are removed; the Orcanos projects are freed.`)) return;
    await fetch(`/api/knowledge/products/${productId}`, {
      method: 'DELETE', credentials: 'include', headers: { 'X-CSRF-Token': getCsrfToken() },
    });
    navigate('/trace/knowledge');
  };

  if (loading) return <div className="pk-page"><div className="pk-loading">Loading…</div></div>;
  if (!product) return (
    <div className="pk-page">
      <div className="pk-error">{error || 'Product not found.'}</div>
      <button className="pk-btn" onClick={() => navigate('/trace/knowledge')}>← Products</button>
    </div>
  );

  const includedCount = (product.sources || []).filter(s => s.include).length;
  const hasDoc = (product.document_md || '').trim().length > 0;

  return (
    <div className="pk-page">
      <div className="pk-header">
        <button className="pk-back" onClick={() => navigate('/trace/knowledge')}>← Products</button>
        <h1 className="pk-title"><ProductIcon size={24} /> {product.name}</h1>
        <div className="pk-header-actions">
          {isAdmin && (
            <button className="pk-btn pk-btn-danger" onClick={deleteProduct}>Delete</button>
          )}
        </div>
      </div>

      {!isAdmin && (
        <div className="pk-readonly">
          👁 View only — Product Knowledge is managed by an Orcanos administrator. You can read
          the sources and the knowledge document, but can't make changes.
        </div>
      )}

      <div className="pk-product-projects">
        <span className="pk-muted">Component projects:</span>{' '}
        {(product.project_ids || []).length
          ? product.project_ids.map(nameOf).join(' · ')
          : <span className="pk-muted">none — edit the product to add projects</span>}
      </div>

      {error && <div className="pk-error">{error}</div>}

      <div className="pk-columns">
        {/* Sources */}
        <div className="pk-col pk-col-sources">
          <div className="pk-col-head">Sources <span className="pk-muted">({includedCount} included)</span></div>

          <div className="pk-sources">
            {(product.sources || []).length === 0 && (
              <div className="pk-empty-quiet">No sources yet. Add pasted text or an Orcanos filter below.</div>
            )}
            {(product.sources || []).map(s => (
              editSrcId === s.id ? (
                <div key={s.id} className="pk-source pk-source-editing">
                  <input className="pk-input" value={editSrcTitle} placeholder="Source title"
                    onChange={e => setEditSrcTitle(e.target.value)} />
                  <textarea className="pk-textarea" rows={6} value={editSrcText}
                    onChange={e => setEditSrcText(e.target.value)} />
                  <div className="pk-source-edit-actions">
                    <button className="pk-btn" onClick={cancelEditSource}>Cancel</button>
                    <button className="pk-btn pk-btn-primary" onClick={saveSourceEdit} disabled={savingEdit}>
                      {savingEdit ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
              <div key={s.id} className={`pk-source ${s.include ? '' : 'pk-source-off'}`}>
                <label className="pk-source-include" title={isAdmin ? 'Include in the document' : 'Included in the document'}>
                  <input type="checkbox" checked={!!s.include} disabled={!isAdmin} onChange={() => toggleInclude(s)} />
                </label>
                <div className="pk-source-body">
                  <div className="pk-source-title">
                    <span className="pk-source-kind">{s.kind}</span>{' '}
                    {s.link
                      ? <a href={s.link} target="_blank" rel="noopener noreferrer" className="pk-source-link">{s.title} ↗</a>
                      : s.title}
                  </div>
                  <div className="pk-source-meta">{s.char_count.toLocaleString()} chars{s.origin ? ` · ${s.origin}` : ''}</div>
                </div>
                <div className="pk-source-actions">
                  {(s.kind === 'text' || s.kind === 'file') && (
                    <button className="pk-source-act" title="View text" onClick={() => setViewSource(s)}>View</button>
                  )}
                  {isAdmin && s.kind === 'text' && (
                    <button className="pk-source-act" title="Edit" onClick={() => startEditSource(s)}>✎</button>
                  )}
                  {isAdmin && (
                    <button className="pk-source-del" title="Remove" onClick={() => deleteSource(s)}>×</button>
                  )}
                </div>
              </div>
              )
            ))}
          </div>

          {isAdmin && (
          <div className="pk-add-source">
            <div className="pk-add-tabs">
              <button className={`pk-tab ${srcKind === 'text' ? 'active' : ''}`} onClick={() => setSrcKind('text')}>Text</button>
              <button className={`pk-tab ${srcKind === 'orcanos' ? 'active' : ''}`} onClick={() => setSrcKind('orcanos')}>Orcanos filter</button>
              <button className={`pk-tab ${srcKind === 'url' ? 'active' : ''}`} onClick={() => setSrcKind('url')}>URL</button>
              <button className={`pk-tab ${srcKind === 'file' ? 'active' : ''}`} onClick={() => setSrcKind('file')}>Upload file</button>
            </div>
            <input className="pk-input" placeholder="Source title (optional)" value={srcTitle} onChange={e => setSrcTitle(e.target.value)} />
            {srcKind === 'text' && (
              <textarea className="pk-textarea" rows={5} placeholder="Paste specs, notes, requirements…" value={srcText} onChange={e => setSrcText(e.target.value)} />
            )}
            {srcKind === 'url' && (
              <>
                <input className="pk-input" placeholder="https://example.com/product-spec" value={srcUrl} onChange={e => setSrcUrl(e.target.value)} />
                <div className="pk-source-meta">The page is fetched and stripped to text. Only public URLs; internal/private addresses are refused.</div>
              </>
            )}
            {srcKind === 'file' && (
              <>
                <input className="pk-file-input" type="file" accept=".pdf,.docx,.txt,.md,.markdown"
                  onChange={e => setSrcFile(e.target.files?.[0] || null)} />
                <div className="pk-source-meta">PDF, Word (.docx), .txt or .md. The file is extracted to text; the original is never stored.</div>
              </>
            )}
            {srcKind === 'orcanos' && (
              <div className="pk-orc-grid">
                <select className="pk-input" value={orcProject} onChange={e => { setOrcProject(e.target.value); setOrcType(''); }}>
                  <option value="">Project…</option>
                  {(product.project_ids || []).map(id => <option key={id} value={id}>{nameOf(id)}</option>)}
                </select>
                <select className="pk-input" value={orcType} onChange={e => setOrcType(e.target.value)} disabled={!orcProject}>
                  <option value="">Item type…</option>
                  {orcItemTypes.map(it => <option key={it.code} value={it.code}>{it.label}</option>)}
                </select>
                <input className="pk-input" placeholder="Filter ID" value={orcFilter} onChange={e => setOrcFilter(e.target.value.replace(/[^0-9]/g, ''))} />
              </div>
            )}
            <button className="pk-btn pk-btn-primary" onClick={addSource} disabled={addingSource}>
              {addingSource ? 'Adding…' : '+ Add source'}
            </button>
          </div>
          )}
        </div>

        {/* Document */}
        <div className="pk-col pk-col-doc">
          <div className="pk-col-head pk-doc-head">
            <span>Knowledge document</span>
            <div className="pk-doc-controls">
              {hasDoc && isAdmin && (
                <div className="pk-toggle" role="group">
                  <button className={docMode === 'view' ? 'active' : ''} onClick={() => setDocMode('view')}>View</button>
                  <button className={docMode === 'edit' ? 'active' : ''} onClick={() => { setDraftDoc(product.document_md || ''); setDocMode('edit'); }}>Edit</button>
                </div>
              )}
              {hasDoc && isAdmin && (
                <button className="pk-btn" onClick={addDocAsSource}
                  title="Save this document as a reusable text source (added un-included so it won't feed its own regeneration)">
                  + Add as source
                </button>
              )}
              {isAdmin && (
                <button className="pk-btn pk-btn-primary" onClick={generate} disabled={busy === 'generating' || includedCount === 0} title={includedCount === 0 ? 'Add a source first' : ''}>
                  {busy === 'generating' ? 'Generating…' : (hasDoc ? '↻ Regenerate' : '✨ Generate document')}
                </button>
              )}
            </div>
          </div>

          {product.is_stale && hasDoc && (
            <div className="pk-stale-nudge">Sources changed since this document was generated — regenerate to refresh it.</div>
          )}

          {proposal !== null && (
            <div className="pk-proposal">
              <div className="pk-proposal-head">
                Proposed document — your current one was hand-edited, so this won't overwrite it automatically.
                <div>
                  <button className="pk-btn" onClick={() => setProposal(null)}>Discard</button>
                  <button className="pk-btn pk-btn-primary" onClick={acceptProposal} disabled={busy === 'saving'}>Use this</button>
                </div>
              </div>
              <div className="pk-doc-view pk-proposal-body">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{proposal}</ReactMarkdown>
              </div>
            </div>
          )}

          {!hasDoc && proposal === null ? (
            <div className="pk-empty">
              {!isAdmin
                ? 'No knowledge document yet. An Orcanos administrator can generate one from the sources.'
                : includedCount === 0
                  ? 'Add at least one source, then generate the knowledge document.'
                  : 'No document yet. Click ✨ Generate document to build it from the sources.'}
            </div>
          ) : docMode === 'edit' ? (
            <div className="pk-doc-edit">
              <textarea className="pk-doc-textarea" value={draftDoc} onChange={e => setDraftDoc(e.target.value)} spellCheck={false} />
              <div className="pk-doc-edit-actions">
                <button className="pk-btn" onClick={() => { setDraftDoc(product.document_md || ''); setDocMode('view'); }}>Cancel</button>
                <button className="pk-btn pk-btn-primary" onClick={saveDoc} disabled={busy === 'saving'}>{busy === 'saving' ? 'Saving…' : 'Save document'}</button>
              </div>
            </div>
          ) : hasDoc ? (
            <div className="pk-doc-view">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{product.document_md}</ReactMarkdown>
              {product.updated_by && (
                <div className="pk-doc-footer pk-muted">
                  Last updated by {product.updated_by}{product.doc_edited_by_user ? ' (hand-edited)' : ' (generated)'}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {viewSource && (
        <div className="pk-modal-backdrop" onClick={() => setViewSource(null)}>
          <div className="pk-modal pk-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="pk-modal-head">
              <div className="pk-modal-title">
                <span className="pk-source-kind">{viewSource.kind}</span> {viewSource.title}
              </div>
              <button className="pk-modal-close" onClick={() => setViewSource(null)}>×</button>
            </div>
            {viewSource.kind === 'file' && (
              <div className="pk-modal-note">Showing the text extracted from the file. The original file is not retained.</div>
            )}
            <pre className="pk-modal-body">{viewSource.content}</pre>
            <div className="pk-modal-actions">
              <button className="pk-btn" onClick={() => downloadSourceText(viewSource)}>⬇ Download .txt</button>
              <button className="pk-btn pk-btn-primary" onClick={() => setViewSource(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
